import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { api } from '../../services/api';
import { usePricing } from '../../context/PricingContext';

interface PricingRule {
  minQuantity: number;
  discount: number;
}

interface PricingSettings {
  taxRate: number;
  volumeDiscounts: PricingRule[];
  minimumOrderAmount: number;
}

const AdminPricing: React.FC = () => {
  const { refreshPricing } = usePricing();
  const [settings, setSettings] = useState<PricingSettings>({
    taxRate: 0.08,
    volumeDiscounts: [],
    minimumOrderAmount: 20,
  });

  const [newDiscount, setNewDiscount] = useState<PricingRule>({
    minQuantity: 0,
    discount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const pricingSettings = await api.settings.getPricing();
        if (pricingSettings) {
          setSettings({
            taxRate: pricingSettings.taxRate || 0.08,
            volumeDiscounts: pricingSettings.volumeDiscounts || [],
            minimumOrderAmount: pricingSettings.minimumOrderAmount || 20,
          });
        }
      } catch (err) {
        console.error('Failed to load pricing settings:', err);
        setError('Failed to load pricing settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSettingsChange = (field: keyof PricingSettings, value: number) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addVolumeDiscount = () => {
    if (newDiscount.minQuantity > 0 && newDiscount.discount > 0) {
      setSettings(prev => ({
        ...prev,
        volumeDiscounts: [...prev.volumeDiscounts, newDiscount]
          .sort((a, b) => a.minQuantity - b.minQuantity),
      }));
      setNewDiscount({ minQuantity: 0, discount: 0 });
    }
  };

  const removeVolumeDiscount = (index: number) => {
    setSettings(prev => ({
      ...prev,
      volumeDiscounts: prev.volumeDiscounts.filter((_, i) => i !== index),
    }));
  };

  const updateVolumeDiscount = (index: number, field: keyof PricingRule, value: number) => {
    setSettings(prev => ({
      ...prev,
      volumeDiscounts: prev.volumeDiscounts.map((discount, i) =>
        i === index ? { ...discount, [field]: value } : discount
      ),
    }));
  };

  const saveSettings = async () => {
    try {
      setError(null);
      await api.settings.updatePricing(settings);
      // Refresh the pricing context to update the app with new settings
      await refreshPricing();
      alert('Pricing settings saved successfully!');
    } catch (err) {
      console.error('Failed to save pricing settings:', err);
      setError('Failed to save pricing settings');
      alert('Failed to save pricing settings. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
          <p className="mt-2 text-gray-600">Loading pricing settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin
        </Link>
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold">Pricing Configuration</h1>
          <button
            onClick={saveSettings}
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
          >
            <Save className="h-5 w-5 mr-2" />
            Save Settings
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">General Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Rate (%)
              </label>
              <input
                type="number"
                value={settings.taxRate * 100}
                onChange={(e) => handleSettingsChange('taxRate', parseFloat(e.target.value) / 100)}
                step="0.01"
                min="0"
                max="100"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Current rate: {(settings.taxRate * 100).toFixed(2)}%
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Order Amount ($)
              </label>
              <input
                type="number"
                value={settings.minimumOrderAmount}
                onChange={(e) => handleSettingsChange('minimumOrderAmount', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Orders below ${settings.minimumOrderAmount.toFixed(2)} will have a fee added to reach the minimum
              </p>
            </div>

          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">Volume Discounts</h2>
          
          <div className="space-y-4 mb-6">
            {settings.volumeDiscounts.map((discount, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Quantity
                  </label>
                  <input
                    type="number"
                    value={discount.minQuantity}
                    onChange={(e) => updateVolumeDiscount(index, 'minQuantity', parseInt(e.target.value))}
                    min="1"
                    className="w-full px-3 py-1 border rounded focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount (%)
                  </label>
                  <input
                    type="number"
                    value={discount.discount * 100}
                    onChange={(e) => updateVolumeDiscount(index, 'discount', parseFloat(e.target.value) / 100)}
                    step="0.01"
                    min="0"
                    max="100"
                    className="w-full px-3 py-1 border rounded focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <button
                  onClick={() => removeVolumeDiscount(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-medium mb-4">Add New Volume Discount</h3>
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Quantity
                </label>
                <input
                  type="number"
                  value={newDiscount.minQuantity || ''}
                  onChange={(e) => setNewDiscount(prev => ({ ...prev, minQuantity: parseInt(e.target.value) || 0 }))}
                  min="1"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount (%)
                </label>
                <input
                  type="number"
                  value={newDiscount.discount * 100 || ''}
                  onChange={(e) => setNewDiscount(prev => ({ ...prev, discount: parseFloat(e.target.value) / 100 || 0 }))}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <button
                onClick={addVolumeDiscount}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">Pricing Preview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-medium mb-2">Current Volume Discounts</h3>
            <div className="space-y-2">
              {settings.volumeDiscounts.map((discount, index) => (
                <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>{discount.minQuantity}+ items</span>
                  <span className="text-green-600">{(discount.discount * 100).toFixed(0)}% off</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-2">Example Calculation</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Base Price (1 item @ $50):</span>
                <span>$50.00</span>
              </div>
              <div className="flex justify-between">
                <span>Tax ({(settings.taxRate * 100).toFixed(1)}%):</span>
                <span>${(50 * settings.taxRate).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-2">
                <span>Total:</span>
                <span>
                  ${(50 * (1 + settings.taxRate)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;